import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { UserPreferencesModel, UserPreferencesModelInput } from '@utmost.co/enterprise-models';
import { DataTableTypes } from '@utmost.co/utmost-design-system';
import { useDeepMemo } from '@beeline.com/bp-design-system';
import { DataTableColumnConfiguration, DataTableKey } from './ColumnConfiguration.types';
import { UPDATE_COLUMN_CONFIGURATIONS } from './ColumnConfiguration.mutations';
import { useColumnConfigurationContext } from './ColumnConfigurationContext';
import { updateAllColumnConfigs } from './updateAllColumnConfigs';

interface InitialState extends DataTableColumnConfiguration {
  pageIndex?: number;
  pageSize?: number;
}

export const useDataTableColumnConfigurations = (
  dataTableKey: DataTableKey,
  initialState?: InitialState | undefined
) => {
  const { allColumnConfigurations, updateLocallyModel } = useColumnConfigurationContext();

  const [updateColumnConfiguration] = useMutation<
    { patchUserPreferences: UserPreferencesModel },
    {
      userPreferences: DeepPartial<UserPreferencesModelInput>;
    }
  >(UPDATE_COLUMN_CONFIGURATIONS);

  const patchColumnConfiguration = useCallback(
    async (updatedColumnConfigurations: DataTableColumnConfiguration) => {
      const updatedConfigurations = updateAllColumnConfigs(
        allColumnConfigurations,
        updatedColumnConfigurations,
        dataTableKey
      );
      updateLocallyModel(updatedConfigurations);
      await updateColumnConfiguration({
        variables: {
          userPreferences: {
            dataTableColumnConfigurations: updatedConfigurations
          }
        }
      });
    },
    [allColumnConfigurations, dataTableKey, updateColumnConfiguration, updateLocallyModel]
  );

  const columnConfigurationMemo: DataTableTypes.InitialState | undefined = useDeepMemo(():
    | DataTableTypes.InitialState
    | undefined => {
    const columnConfiguration = allColumnConfigurations?.find(({ key }) => key === dataTableKey);
    // set initialState if provided (mostly from user interface setting page)
    if (initialState) {
      return {
        ...(initialState || {}),
        columnSizing: (!!columnConfiguration?.columnSizing && Object.keys(columnConfiguration.columnSizing).length === 0
          ? (initialState.columnSizing ?? {})
          : (columnConfiguration?.columnSizing ?? {})) as Record<string, number>,
        columnOrder:
          !!columnConfiguration?.columnOrder && columnConfiguration.columnOrder.length === 0
            ? (initialState.columnOrder ?? [])
            : (columnConfiguration?.columnOrder ?? []),
        hiddenColumns:
          !!columnConfiguration?.hiddenColumns && columnConfiguration.hiddenColumns.length === 0
            ? (initialState.hiddenColumns ?? [])
            : (columnConfiguration?.hiddenColumns ?? [])
      };
    }
    return columnConfiguration as DataTableTypes.InitialState | undefined;
  }, [allColumnConfigurations, dataTableKey, initialState]);

  return {
    columnConfiguration: columnConfigurationMemo,
    patchColumnConfiguration
  };
};
